import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useReducedMotion,
} from 'motion/react';
import { Check, TriangleAlert } from 'lucide-react';
import { useBlockerFull } from '@/hooks/use-blocker-full';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import { ImportStage } from '@/components/campaign-import/import-stage';
import type { WizardStep } from '@/components/campaign-import/import-wizard.reducer';

const INDICATOR_STEPS = ['file', 'mapping', 'review', 'confirm'] as const;

/** Nothing leaves the browser before the confirm step, so leaving is only costly in between. */
export function shouldWarnBeforeLeave(step: WizardStep): boolean {
  return step === 'mapping' || step === 'review' || step === 'confirm';
}

function useStepCopy() {
  const { LL } = useI18nContext();
  return {
    labels: {
      file: LL.campaignImport.steps.file(),
      mapping: LL.campaignImport.steps.mapping(),
      review: LL.campaignImport.steps.review(),
      confirm: LL.campaignImport.steps.confirm(),
    },
    titles: {
      file: LL.campaignImport.stepTitles.file(),
      mapping: LL.campaignImport.stepTitles.mapping(),
      review: LL.campaignImport.stepTitles.review(),
      confirm: LL.campaignImport.stepTitles.confirm(),
      result: LL.campaignImport.stepTitles.result(),
    },
    descriptions: {
      file: LL.campaignImport.stepDescriptions.file(),
      mapping: LL.campaignImport.stepDescriptions.mapping(),
      review: LL.campaignImport.stepDescriptions.review(),
      confirm: LL.campaignImport.stepDescriptions.confirm(),
      result: LL.campaignImport.stepDescriptions.result(),
    },
  } satisfies {
    labels: Record<(typeof INDICATOR_STEPS)[number], string>;
    titles: Record<WizardStep, string>;
    descriptions: Record<WizardStep, string>;
  };
}

type IndicatorState = 'complete' | 'current' | 'upcoming';

function indicatorState(
  indicatorStep: (typeof INDICATOR_STEPS)[number],
  step: WizardStep
): IndicatorState {
  if (step === 'result') return 'complete';
  const diff =
    INDICATOR_STEPS.indexOf(indicatorStep) - INDICATOR_STEPS.indexOf(step);
  if (diff < 0) return 'complete';
  return diff === 0 ? 'current' : 'upcoming';
}

function StepIndicator({ step }: { step: WizardStep }) {
  const { LL } = useI18nContext();
  const { labels } = useStepCopy();

  return (
    <nav aria-label={LL.campaignImport.stepsLabel()}>
      <ol className="flex items-center">
        {INDICATOR_STEPS.map((indicatorStep, index) => {
          const state = indicatorState(indicatorStep, step);
          return (
            <li
              key={indicatorStep}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                'flex items-center',
                index < INDICATOR_STEPS.length - 1 && 'flex-1'
              )}
            >
              <span className="flex items-center gap-2.5">
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors duration-300',
                    state === 'complete' &&
                      'border-[#155DFC] bg-[#155DFC] text-white',
                    state === 'current' &&
                      'border-[#155DFC] bg-[#155DFC]/15 text-white ring-4 ring-[#155DFC]/25',
                    state === 'upcoming' && 'border-white/20 text-white/50'
                  )}
                >
                  {state === 'complete' ? (
                    <Check className="size-3.5" aria-hidden />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={cn(
                    'text-sm font-medium transition-colors duration-300 lg:inline',
                    state === 'current' ? 'inline' : 'hidden',
                    state === 'upcoming' ? 'text-white/50' : 'text-white'
                  )}
                >
                  {labels[indicatorStep]}
                </span>
                <span
                  className={cn(
                    'sr-only',
                    state === 'current' ? 'hidden' : 'lg:hidden'
                  )}
                >
                  {labels[indicatorStep]}
                </span>
              </span>
              {index < INDICATOR_STEPS.length - 1 ? (
                <span
                  aria-hidden
                  className="relative mx-3 h-px flex-1 overflow-hidden bg-white/15"
                >
                  <motion.span
                    className="absolute inset-0 origin-left bg-[#155DFC]"
                    initial={false}
                    animate={{ scaleX: state === 'complete' ? 1 : 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  />
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function StepSection({
  step,
  focusTitle,
  children,
}: {
  step: WizardStep;
  focusTitle: boolean;
  children: ReactNode;
}) {
  const { LL } = useI18nContext();
  const { titles, descriptions } = useStepCopy();
  const reduceMotion = useReducedMotion();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusTitle) titleRef.current?.focus();
  }, [focusTitle]);

  const position = INDICATOR_STEPS.indexOf(
    step === 'result' ? 'confirm' : step
  );

  return (
    <motion.section
      data-transition={reduceMotion ? 'fade' : 'slide'}
      aria-labelledby="campaign-import-step-title"
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -12 }}
      transition={{
        duration: reduceMotion ? 0.12 : 0.4,
        ease: [0.16, 1, 0.3, 1],
      }}
      className="rounded-xl border border-white/10 bg-white/[0.05] p-6 backdrop-blur-md md:p-8"
    >
      <p className="sr-only">
        {LL.campaignImport.stepOf({
          current: position + 1,
          total: INDICATOR_STEPS.length,
        })}
      </p>
      <h2
        ref={titleRef}
        id="campaign-import-step-title"
        tabIndex={-1}
        className="text-xl font-semibold tracking-tight outline-none md:text-2xl"
      >
        {titles[step]}
      </h2>
      <p className="mt-1.5 max-w-prose text-sm text-white/65">
        {descriptions[step]}
      </p>
      <div className="mt-6">{children}</div>
    </motion.section>
  );
}

export interface ImportWizardPresenterProps {
  step: WizardStep;
  /** False blocks the whole import: links can only point to the store's website. */
  websiteDomainConfigured: boolean;
  children?: ReactNode;
}

export function ImportWizardPresenter({
  step,
  websiteDomainConfigured,
  children,
}: ImportWizardPresenterProps) {
  const { LL } = useI18nContext();
  useBlockerFull(shouldWarnBeforeLeave(step));

  // The first title is not focused: the page was just opened. Later ones are, after the
  // previous step has left, which is when the new section mounts.
  const previousStep = useRef(step);
  const [hasChangedStep, setHasChangedStep] = useState(false);
  useEffect(() => {
    if (previousStep.current !== step) setHasChangedStep(true);
    previousStep.current = step;
  }, [step]);

  return (
    <MotionConfig reducedMotion="user">
      <ImportStage className="flex shrink-0 flex-col gap-8 p-6 md:p-10">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            {LL.campaignImport.pageTitle()}
          </h1>
          <p className="text-sm text-white/65">
            {LL.campaignImport.pageSubtitle()}
          </p>
        </header>

        {websiteDomainConfigured ? (
          <>
            <StepIndicator step={step} />
            <AnimatePresence mode="wait">
              <StepSection key={step} step={step} focusTitle={hasChangedStep}>
                {children}
              </StepSection>
            </AnimatePresence>
          </>
        ) : (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-xl border border-amber-300/30 bg-amber-300/10 p-5"
          >
            <TriangleAlert
              className="mt-0.5 size-5 shrink-0 text-amber-300"
              aria-hidden
            />
            <div className="space-y-1">
              <p className="font-semibold">
                {LL.campaignImport.websiteDomainBlockedTitle()}
              </p>
              <p className="text-sm text-white/75">
                {LL.createCampaign.validation.websiteDomainNotConfigured()}
              </p>
            </div>
          </div>
        )}
      </ImportStage>
    </MotionConfig>
  );
}
